// Math-gate parental lock — see spec §D2 / Prompt 9, reworked by MA-853
// (PUX-2, OPERATOR DECISION D-PUX-3).
// "What is a × b?" (a fresh random product each mount: a in 12-24, b in 3-4,
// so every answer is 36-96 and the pad's two-digit submit still works) —
// numeric pad — 3 wrong → "Ask a grown-up" plus a 60 s cool-down kept in
// sessionStorage that "Try again" (or Cancel + reopen, or a reload) cannot skip.
// The unlock persists 3 minutes (GATE_TTL_MS) in sessionStorage.

// Short TTL — just long enough to navigate menu → dashboard → a settings
// subpage. MA-853: leaving the parent area for a kid surface clears it
// (noteGatePath below, called by App.jsx on every route change); the legal
// pages and /onboarding leave it alone.
const GATE_TTL_MS = 3 * 60 * 1000;
export const GATE_MAX_WRONG = 3;
export const GATE_COOLDOWN_MS = 60 * 1000;

const KEY = "pencilwise-parent-gate-unlocked-until";
const MISSES_KEY = "pencilwise-parent-gate-misses";
const COOLDOWN_KEY = "pencilwise-parent-gate-cooldown-until";
const AREA_KEY = "pencilwise-parent-area-entered";
// MA-855 (D-PUX-3): set by an explicit "Hand to kid". localStorage, not
// sessionStorage, so it survives closing the tab or relaunching the app.
export const REARM_KEY = "pencilwise-parent-gate-rearm";

export const GATE_A_MIN = 12;
export const GATE_A_MAX = 24;
export const GATE_B_MIN = 3;
export const GATE_B_MAX = 4;

export interface GateQuestion {
  a: number;
  b: number;
  answer: number;
}

type Rng = () => number;

/** Seeded PRNG (mulberry32) — deterministic sequences for tests. */
export function mulberry32(seed: number): Rng {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

let testRng: Rng | null = null;

/** Node-test seam: force the gate's RNG (null restores the default). */
export function setParentGateRngForTests(rng: Rng | null): void {
  testRng = rng;
}

/**
 * The RNG the gate uses. Order: the node-test seam, then the e2e seam
 * `window.__pwParentGateRng` — read only when `import.meta.env.DEV` is true.
 * Vite replaces that with the literal `false` in a production build, so the
 * whole branch (and the hook's name) is removed from the bundle (checked
 * with `npx vite build` + grep). Under the node/tsx test runner
 * `import.meta.env` is undefined, hence the optional chain.
 */
export function gateRng(): Rng {
  if (testRng) return testRng;
  if (import.meta.env?.DEV) {
    if (typeof window !== "undefined") {
      const hook = (window as unknown as { __pwParentGateRng?: unknown }).__pwParentGateRng;
      if (typeof hook === "function") return hook as Rng;
    }
  }
  return Math.random;
}

function pick(r: number, min: number, max: number): number {
  const x = Number.isFinite(r) ? Math.min(Math.max(r, 0), 0.999999) : 0;
  return min + Math.floor(x * (max - min + 1));
}

/** A fresh gate question. Bad rng output is clamped into range. */
export function makeGateQuestion(rng: Rng = gateRng()): GateQuestion {
  const a = pick(rng(), GATE_A_MIN, GATE_A_MAX);
  const b = pick(rng(), GATE_B_MIN, GATE_B_MAX);
  return { a, b, answer: a * b };
}

function store(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readNum(key: string): number {
  const s = store();
  if (!s) return 0;
  try {
    const n = Number(s.getItem(key));
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function write(key: string, value: string | null): void {
  const s = store();
  if (!s) return;
  try {
    if (value === null) s.removeItem(key);
    else s.setItem(key, value);
  } catch {
    /* private mode / quota */
  }
}

export function isParentGateUnlocked(): boolean {
  if (typeof window === "undefined") return false;
  // MA-855 RED r1 MUST-1: while a hand-off is armed no unlock counts. The
  // unlock lives in per-tab sessionStorage, so another tab can still hold a
  // live stamp; every real gate pass (unlockParentGate) clears the flag, so
  // "armed and unlocked" is always a stale stamp.
  if (isHandoffRearmed()) return false;
  const raw = window.sessionStorage.getItem(KEY);
  if (!raw) return false;
  return Number(raw) > Date.now();
}

export function unlockParentGate() {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(KEY, String(Date.now() + GATE_TTL_MS));
  // MA-855 (D-PUX-3): a grown-up passing the gate once clears the hand-off
  // re-arm flag. The personal-device auto-unlock only reaches this line when
  // the flag is already clear (personalBypassAllowed), so it never disarms.
  writeLocal(REARM_KEY, null);
  // MA-853 RED round 1 MUST-1: any grown-up unlock arms the relock, including
  // one earned on a kid route (the kiosk roster's exit on /play/who), so the
  // next kid surface after "Back to class" or "Disarm" clears it.
  write(AREA_KEY, "1");
}

/** MA-853: clear the unlock so the next /parent or /teacher entry asks again. */
export function relockParentGate(): void {
  write(KEY, null);
}

/** Milliseconds left on the three-miss cool-down (0 when none). */
export function gateCooldownRemainingMs(now: number = Date.now()): number {
  const until = readNum(COOLDOWN_KEY);
  return until > now ? until - now : 0;
}

/** Misses recorded since the last cool-down or correct answer. */
export function gateMisses(): number {
  return Math.max(0, Math.floor(readNum(MISSES_KEY)));
}

/**
 * Record one wrong answer. The GATE_MAX_WRONG-th miss starts the cool-down
 * (stored in sessionStorage, so a remount or reload cannot skip it) and
 * resets the miss count for the next round.
 */
export function recordGateMiss(now: number = Date.now()): { misses: number; cooldownUntil: number } {
  const misses = gateMisses() + 1;
  if (misses >= GATE_MAX_WRONG) {
    const until = now + GATE_COOLDOWN_MS;
    write(COOLDOWN_KEY, String(until));
    write(MISSES_KEY, null);
    return { misses: GATE_MAX_WRONG, cooldownUntil: until };
  }
  write(MISSES_KEY, String(misses));
  return { misses, cooldownUntil: 0 };
}

/** A correct answer clears the miss count and any expired cool-down. */
export function clearGateMisses(): void {
  write(MISSES_KEY, null);
  write(COOLDOWN_KEY, null);
}

export type GatePathKind = "parent" | "kid" | "neutral";

const PARENT_ROOTS = ["/parent", "/teacher"];
const KID_ROOTS = ["/play", "/read", "/library", "/journey", "/pal"];

function under(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

/**
 * MA-853 route classes. Parent area: /parent, /teacher (+ sub-routes). Kid
 * surfaces: /play (+ sub-routes, incl. /play/who), /read, /library,
 * /journey, /pal. Everything else (the legal pages /privacy, /terms,
 * /coppa-notice, /dpa, /onboarding, …) is neutral. /concept (the concept
 * card) is neutral too; it only leads on to /play/session, which relocks.
 */
export function classifyGatePath(pathname: string): GatePathKind {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  if (PARENT_ROOTS.some((r) => under(p, r))) return "parent";
  if (KID_ROOTS.some((r) => under(p, r))) return "kid";
  return "neutral";
}

/**
 * Called on every route change (App.jsx). Entering the parent area, or any
 * grown-up unlock (unlockParentGate), sets a sessionStorage marker; arriving at a kid surface with the marker set
 * relocks and clears it; a neutral page changes nothing — so parent →
 * /privacy → parent stays open, while parent → /privacy → /play relocks.
 * Returns true when it relocked.
 */
export function noteGatePath(pathname: string): boolean {
  const kind = classifyGatePath(pathname);
  if (kind === "parent") {
    write(AREA_KEY, "1");
    return false;
  }
  if (kind === "kid" && readNum(AREA_KEY) === 1) {
    relockParentGate();
    write(AREA_KEY, null);
    return true;
  }
  return false;
}

function local(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null): void {
  const s = local();
  if (!s) return;
  try {
    if (value === null) s.removeItem(key);
    else s.setItem(key, value);
  } catch {
    /* private mode / quota */
  }
}

/**
 * MA-855 (D-PUX-2, D-PUX-3): an explicit hand-off to a child. Sets the
 * localStorage re-arm flag and clears the unlock, so the next /parent or
 * /teacher entry shows the gate — on a personal device too, and after a
 * relaunch through the "/" front door — until a grown-up passes it once
 * (unlockParentGate clears the flag).
 */
export function armHandoffRelock(): void {
  writeLocal(REARM_KEY, "1");
  relockParentGate();
}

/** True while a hand-off's re-arm flag is set (storage failure → false). */
export function isHandoffRearmed(): boolean {
  const s = local();
  if (!s) return false;
  try {
    return s.getItem(REARM_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * MA-03 personal-device bypass, MA-855 edition: a personal device skips the
 * gate unless it was handed to a child through the app and no grown-up has
 * passed the gate since. A device that never handed off keeps today's
 * behaviour (the parent's own phone opens on Progress).
 */
export function personalBypassAllowed(mode: string): boolean {
  return mode === "personal" && !isHandoffRearmed();
}

/**
 * MA-855 RED r1 SHOULD-1: a parent area already open in another tab must
 * close when this device is handed to a child. localStorage writes fire a
 * `storage` event in every OTHER tab of the origin; on the re-arm flag being
 * set, call `onArmed` (the layouts drop back to the gate). Returns the
 * unsubscribe function.
 */
export function onHandoffArmedElsewhere(onArmed: () => void): () => void {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") return () => {};
  const handler = (e: StorageEvent) => {
    if (e.key === REARM_KEY && e.newValue === "1") onArmed();
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
